/**
 * iter447 · Report / Block menu for user-generated content items.
 * ─────────────────────────────────────────────────────────────────
 * Powers Apple Guideline 1.2 compliance across every UGC surface
 * (DM messages, story comments, athlete profiles, network posts,
 * etc.). Drop it next to any user-generated tile:
 *
 *   <ReportBlockMenu
 *     kind="message"
 *     targetId={m.id}
 *     targetUserId={m.sender_user_id}
 *     targetLabel={m.sender_name}
 *   />
 *
 * It renders an unobtrusive "⋮" trigger. Tap → tiny dropdown → tap
 * again → prompt for a reason (Report) or one-tap confirm (Block).
 * Backend calls hit /api/reports and /api/me/block-user, both of
 * which require auth and are gated by the moderation router.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MoreVertical, Flag, UserX, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportBlockMenu({
  kind,           // 'message' | 'comment' | 'story' | 'athlete' | 'agent' | 'user'
  targetId,       // id of the reported item
  targetUserId,   // user_id of the AUTHOR (needed for Block + eject)
  targetLabel,    // display name for confirm dialogs
  onBlocked,      // optional callback after successful block (parent
                  //   can remove the item from its list instantly per
                  //   Apple's "instant removal" spec)
  // iter516l · Default align changed from 'right' to 'left'. Report
  // buttons in this app all live on the LEFT edge of their parent
  // rows (network profile, story, message thread). `align='right'`
  // positioned the dropdown's right edge at the button's right edge,
  // pushing the 160-px-wide dropdown off-screen on narrow iPhones.
  // `align='left'` opens the dropdown going RIGHT (safer default).
  // Pass align='right' explicitly if the button ever sits on the
  // right edge of a row.
  align = 'left',
  label = null,   // iter513 · optional visible text (e.g. "Report") so
                  // the trigger reads more clearly on iOS screen
                  // recordings. Falls back to the icon-only style.
}) {
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const closeAll = useCallback(() => {
    setOpen(false); setReportOpen(false); setReason('');
  }, []);

  const submitReport = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      toast.error('Add a short reason so our team can review.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/reports`, {
        kind,
        target_id: String(targetId || ''),
        target_user_id: targetUserId || null,
        reason: trimmed,
      }, { withCredentials: true });
      toast.success('Thanks — our team will review within 24 hours.');
      closeAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send report. Please try again.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const doBlock = async () => {
    if (!targetUserId) {
      toast.error('Cannot block — user id missing.');
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Block ${targetLabel || 'this user'}? You won't see their content anymore.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/me/block-user`, { user_id: targetUserId }, { withCredentials: true });
      toast.success(`Blocked ${targetLabel || 'user'}. Their content is hidden from your feed.`);
      closeAll();
      if (typeof onBlocked === 'function') onBlocked(targetUserId);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not block. Please try again.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block" data-testid={`report-block-menu-${kind}-${targetId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Report or block"
        className={label
          ? "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-white bg-white/[0.08] ring-1 ring-white/15 hover:bg-white/[0.15] active:scale-95 transition"
          : "w-7 h-7 inline-flex items-center justify-center rounded-full text-white/50 hover:text-white/90 hover:bg-white/[0.08] active:scale-90 transition"
        }
        data-testid={`report-block-trigger-${targetId}`}
      >
        <MoreVertical className={label ? "w-2.5 h-2.5" : "w-3.5 h-3.5"} />
        {label && <span>{label}</span>}
      </button>
      {open && !reportOpen && (
        <div
          className={`absolute z-30 mt-1 w-40 max-w-[calc(100vw-2rem)] rounded-lg bg-neutral-950 ring-1 ring-white/15 overflow-hidden shadow-lg ${align === 'right' ? 'right-0' : 'left-0'}`}
          data-testid={`report-block-dropdown-${targetId}`}
        >
          <button
            type="button"
            onClick={() => { setReportOpen(true); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-white hover:bg-white/[0.06] text-left"
            data-testid={`report-btn-${targetId}`}
          >
            <Flag className="w-3.5 h-3.5 text-white/70" />
            Report content
          </button>
          {targetUserId && (
            <button
              type="button"
              onClick={doBlock}
              disabled={busy}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-white hover:bg-white/[0.06] text-left border-t border-white/10 disabled:opacity-60"
              data-testid={`block-btn-${targetId}`}
            >
              <UserX className="w-3.5 h-3.5 text-red-300" />
              Block user
            </button>
          )}
          <button
            type="button"
            onClick={closeAll}
            className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-white/40 hover:bg-white/[0.04] hover:text-white/60 text-left border-t border-white/10"
            data-testid={`report-block-cancel-${targetId}`}
          >
            <X className="w-3 h-3" />
            Cancel
          </button>
        </div>
      )}
      {reportOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={closeAll}
          data-testid={`report-modal-${targetId}`}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-neutral-950 ring-1 ring-white/15 p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <Flag className="w-4 h-4 text-white/80" />
              <p className="font-display uppercase text-sm tracking-tight">Report this content</p>
            </div>
            <p className="text-xs text-white/60 leading-relaxed">
              Our team reviews reports within 24 hours. Content found to violate our community guidelines will be removed and the user may be ejected from Flythouse.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you reporting this? (spam, harassment, hate speech, etc.)"
              rows={4}
              maxLength={280}
              style={{ fontSize: 16 }}
              className="w-full rounded-lg bg-black/60 ring-1 ring-white/15 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:ring-white/40 resize-none"
              data-testid={`report-reason-${targetId}`}
            />
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={closeAll}
                className="text-xs text-white/60 hover:text-white px-4 py-2 rounded-full transition"
                data-testid={`report-cancel-${targetId}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReport}
                disabled={busy || reason.trim().length < 3}
                className="inline-flex items-center gap-1.5 text-black text-xs font-bold uppercase tracking-widest px-5 py-2.5 rounded-full bg-white hover:bg-white/90 disabled:opacity-40 transition active:scale-95"
                data-testid={`report-submit-${targetId}`}
              >
                {busy ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
